import { useMemo, useState } from 'react';

import type { MobileRelayPort } from '@/shared-responder-v2-core';
import {
  useMobileUtSessionGateway,
  useUtRemoteVoiceCommands,
} from '@/shared-survey';

import { createMobileRelayRuntime } from './survey-responder-v2-adapter-mobile-relay';

/**
 * `uniqueId` is `null` for every session running on the responder's own
 * device, which is what makes the same port inert there — nothing subscribes
 * and no mutation is sent.
 *
 * The gateway is captured once, as `useEmbedSessionPort` captures its own:
 * its mutate functions come from `useMutation` and keep their identity for the
 * life of the hook, so only the wrapper object around them is new per render.
 *
 * The port is memoized because the kernel keeps it in an effect's dependency
 * array (`shared-responder-v2-capability-coordination.hooks.ts`), where a new
 * object each render would tear down and re-open the transcript subscription
 * on every render.
 */
export function useSurveyMobileRelayPort(
  uniqueId: string | null
): MobileRelayPort {
  const gateway = useMobileUtSessionGateway();

  const [runtime] = useState(() => createMobileRelayRuntime(gateway));

  useUtRemoteVoiceCommands({ uniqueId, onTurn: runtime.emitTurn });

  return useMemo(() => runtime.portFor(uniqueId), [runtime, uniqueId]);
}
